import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "@modalSrc/Modal";
import { useNavigate } from "react-router-dom";
import { MdClose, MdDelete, MdRefresh, MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { BsQrCode } from "react-icons/bs";
import { FaPlusSquare, FaThumbsUp, FaThumbsDown, FaFileDownload, FaFilePdf, FaUpload } from "react-icons/fa";
import { ImCancelCircle } from "react-icons/im";
import { LuRefreshCcw, LuChevronsDown, LuChevronsUp, LuMonitor, LuClipboardCopy } from "react-icons/lu";
import { MdMonitorHeart } from "react-icons/md";
import { IoIosRefresh } from "react-icons/io";
import { IoTrashOutline } from "react-icons/io5";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import Helpers from "../../Helpers";
import RightTimeApi from "@/API";
import Enums from "../../enums";
import DateTimePicker from "../DateTimePicker";
import '@/style/sass/_testDetailsModal.scss';
import NewEntityModal from "./NewEntityModal";
import ConfirmModal from "./ConfirmModal";
import TestScoreModal from "./TestScoreModal";
import TestInstanceModal from "./TestInstanceModal";
import TestMonitorModal from "./TestMonitorModal";
import ChangeCompanyModal from "./ChangeCompanyModal";
import ReportSelectModal from "./ReportSelectModal";
import QrModal from "./QrModal";
import { set } from "date-fns";
import { copyUrlAsLink } from "@/utils/copyUrlAsLink";
import { generateTestAnswerPdfAsync } from "@/utils/testAnswerReplayPdf";
import { buildTestAnswerPdfDetails, buildTestAnswerPdfFilename } from "@/utils/testAnswerPdfDetails";
import { readPermIdsFromSession } from "@/i18n/sessionUser";
import { downloadAuthenticatedFile, downloadResponseAsFile } from "@/utils/authenticatedFileDownload";
import { apiMessage } from "@/utils/apiMessage";
import { applyProctorLiveUpdate } from "@/utils/applyProctorLiveUpdate";
import { displayClientIp } from "@/utils/displayClientIp";
import { useProctorSignalR } from "@/utils/useProctorSignalR";
import {
  countRestartedRealtimeForSection,
  filterResponsesForSection,
  firstSectionIdWithResponses,
  responseDisplayValue,
  sortRealtimeResponsesForDisplay,
} from "@/utils/testDetailsResponses";
import { buildSectionAnalysisRows } from "@/utils/testDetailsSectionAnalysis";
import { buildCurrentMatchRow, countActiveMatches, matchDetailsInstanceId } from "@/utils/testDetailsMatches";
import { checkTestMatchesAsync } from "@/utils/testMatchCheck";
const baseURL = Helpers.Env("VITE_TEST_RUNNER_URL");

// Used by commented-out Adjectives Scoring tab (`renderScoringContent`).
// eslint-disable-next-line no-unused-vars -- restore Scoring tab alongside renderScoringContent
const ADJECTIVE_LIST = [
  { id: 301, abbrev: "ENT", name: "Enthusiastic",        correct: "S", color: "W" },
  { id: 302, abbrev: "HON", name: "Honest",              correct: "S", color: "W" },
  { id: 303, abbrev: "CAR", name: "Caring",              correct: "T", color: "Y" },
  { id: 304, abbrev: "PER", name: "Perseverant",         correct: "S", color: "W" },
  { id: 305, abbrev: "CON", name: "Confident",           correct: "S", color: "W" },
  { id: 306, abbrev: "PRO", name: "Professional",        correct: "S", color: "W" },
  { id: 307, abbrev: "FCS", name: "Focused",             correct: "T", color: "Y" },
  { id: 308, abbrev: "FOR", name: "Forward-Thinking",    correct: "S", color: "G" },
  { id: 309, abbrev: "ORG", name: "Organized",           correct: "S", color: "W" },
  { id: 310, abbrev: "FLX", name: "Flexible",            correct: "T", color: "G" },
  { id: 311, abbrev: "GOL", name: "Goal-Oriented",       correct: "S", color: "W" },
  { id: 312, abbrev: "OMT", name: "Prompt",              correct: "S", color: "W" },
  { id: 313, abbrev: "DEP", name: "Dependable",          correct: "S", color: "W" },
  { id: 314, abbrev: "ASR", name: "Assertive",           correct: "S", color: "Y" },
  { id: 315, abbrev: "HUM", name: "Good Sense of Humor", correct: "T", color: "Y" },
  { id: 316, abbrev: "POS", name: "Positive",            correct: "S", color: "Y" },
  { id: 317, abbrev: "COM", name: "Committed",           correct: "T", color: "G" },
  { id: 318, abbrev: "EMP", name: "Empathetic",          correct: "T", color: "G" },
] as const;

type ScoreEntry = {
  scoreType?: number;
  scoreTypeName?: string;
  score?: number | string;
  message?: string;
  active?: boolean;
};

type TestSection = {
  id: number;
  sectionId?: number;
  formId?: number;
  index?: number;
  name?: string;
  submitTime?: string;
  ipAddr?: string;
};

type TestResponse = {
  sectionId?: number;
  field?: string | number;
  value?: string | number;
  questionId?: string | number;
  response?: string | number;
};

type RealtimeResponse = {
  id?: number;
  sectionId?: number;
  formId?: number;
  question?: string;
  response?: string;
  time?: string;
  clientTime?: string | null;
};

type MatchRow = {
  testId?: number;
  testInstanceId?: number | null;
  eliminated?: boolean | null;
  scores?: ScoreEntry[];
  [key: string]: unknown;
};

type InstanceReportOption = {
  reportDefinitionId: number;
  name: string;
};

type TestInstanceLite = {
  id: number;
  createTime?: string;
  companyJobName?: string;
  siteName?: string;
  companyTestStatusId?: number;
  statusId?: number;
  companyTestTypeId?: number;
  companyTestStatus?: {
    display?: string;
    name?: string;
  };
  status?: string;
  statusName?: string;
  supervisor?: string;
  scores?: ScoreEntry[];
  /** Active TestInstanceData rows keyed by DataKey (from API). */
  instanceDataActiveByKey?: Record<string, string | null | undefined>;
  companyTestType: {
    id?: number;
    companyTestTypeId?: number;
    name?: string;
    testTypeName?: string;
  };
};

type InvoiceLineItemLite = {
  id?: number;
  chargeDate?: string;
  description?: string;
  companyFeeItemName?: string;
  quantity?: number;
  amount?: number;
  invoiceId?: number;
};

type CompanyTestTypeLite = {
  id?: number;
  companyTestTypeId?: number;
  statuses?: Array<{
    ctsId?: number;
    id?: number;
    statusId?: number;
    display?: string;
    name?: string;
    billable?: boolean;
  }>;
};

type CompanyTestTypeStatusLite = {
  ctsId?: number;
  code?: string;
  description?: string;
  display?: string;
  billable?: boolean;
};

type TestDetailsModel = {
  id?: number;
  codeExpiration?: string;
  codeActive?: boolean;
  code?: string;
  sections?: TestSection[];
  scores?: ScoreEntry[];
  submitTime?: string;
  status?: string;
  archived?: boolean;
  company?: string;
  firstName?: string;
  lastName?: string;
  uniqueId?: string;
  language?: string;
  companyTestType?: {
    id?: number;
    name?: string;
    code?: string;
    codeTimeoutMinutes?: number;
    fields?: Array<{ proctorProvided?: boolean }>;
  };
  companyTestTypeFields?: Array<{ proctorProvided?: boolean }>;
  testTypeName?: string;
  testTypeId?: number;
  location?: string;
  testInstances?: TestInstanceLite[];
  createTime?: string;
  welcomeTime?: string;
  startTime?: string;
  embargoTime?: string;
  data1?: string;
  data2?: string;
  responses?: TestResponse[];
  realtimeResponses?: RealtimeResponse[];
  tags?: string[];
  invoiceLineItems?: InvoiceLineItemLite[];
  matches?: MatchRow[];
  companyTestTypes?: CompanyTestTypeLite[];
  companyTestTypeStatuses?: CompanyTestTypeStatusLite[];
  startTestButtonText?: string;
  canTake?: boolean;
  availableSites?: Array<{ id: number; name: string }>;
  site?: string;
  availableJobs?: Array<{ id: number; name: string }>;
  job?: string;
  companyTestTypeName?: string;
  customFields?: Array<{ name?: string; displayName?: string; value?: string; fieldName?: string }>;
  stateId?: number;
  scoreTime?: string;
};


export default function TestDetailsModal(props) {
  if (!props?.testInstanceId && !props?.testId)
    return null;

  const { onClose, testInstanceId, updateSpinner, isSystemAdmin, initialActiveTab } = props || {};

  const [testUrl, updateTestUrl] = useState("pending...");
  const [test, updateTest] = useState<TestDetailsModel>({});
  const sortedRealtimeResponses = useMemo(
    () => sortRealtimeResponsesForDisplay(test.realtimeResponses),
    [test.realtimeResponses]
  );
  const [activeTab, setActiveTab] = useState("details");
  const [testChanges, setTestChanges] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesLoadedForTestId, setChangesLoadedForTestId] = useState(null);
  const [scoresEditMode, setScoresEditMode] = useState(false);
  const [scoreTypes, setScoreTypes] = useState([]);
  const [editScores, setEditScores] = useState({});
  const [selectedMatchInstanceId, setSelectedMatchInstanceId] = useState<number | null>(null);
  const selectedMatchInstanceIdRef = useRef<number | null>(null);
  selectedMatchInstanceIdRef.current = selectedMatchInstanceId;
  const [showNewEntityModal, setShowNewEntityModal] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetScoresConfirm, setShowResetScoresConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedTestInstanceId, setSelectedTestInstanceId] = useState(null);
  const [testId, setTestId] = useState(null);
  const [showMonitorModal, setShowMonitorModal] = useState(false);
  const [originalDetailsValues, setOriginalDetailsValues] = useState({ createTime: "", codeExpiration: "", embargoTime: "", firstName: "", lastName: "" });
  const [editedDetailsValues, setEditedDetailsValues] = useState({ createTime: "", codeExpiration: "", embargoTime: "", firstName: "", lastName: "" });
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false);
  const [sessionPermIds, setSessionPermIds] = useState<number[]>([]);
  const gradingUploadInputRef = useRef<HTMLInputElement>(null);
  const [gradingUploadTarget, setGradingUploadTarget] = useState<{ testId: number; instanceId: number } | null>(null);
  const [reportPdfSelectOpen, setReportPdfSelectOpen] = useState(false);
  const [reportPdfOptions, setReportPdfOptions] = useState<InstanceReportOption[]>([]);
  const [reportPdfPendingInstanceId, setReportPdfPendingInstanceId] = useState<number | null>(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [qrContent, setQrContent] = useState("");
  const [isGeneratingAnswerPdf, setIsGeneratingAnswerPdf] = useState(false);
  let navigate = useNavigate();

  useProctorSignalR(Boolean(testInstanceId), (message) => {
    updateTest((prev) => applyProctorLiveUpdate(prev, message) ?? prev);
  });

  async function downloadAnswerPdfAsync() {
    if (!testInstanceId) {
      toast.error("Test instance ID not available for answer PDF.");
      return;
    }

    setIsGeneratingAnswerPdf(true);
    if (updateSpinner) updateSpinner(true);
    try {
      await generateTestAnswerPdfAsync(
        testInstanceId,
        buildTestAnswerPdfDetails(test, {
          isSystemAdmin: Boolean(isSystemAdmin),
          showSystemAdminFields: Boolean(isSessionSystemAdmin),
        }),
        buildTestAnswerPdfFilename(test),
      );
      toast.success("Answer PDF downloaded.");
    } catch {
      toast.error("Unable to generate answer PDF.");
    } finally {
      setIsGeneratingAnswerPdf(false);
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function fetchAndSaveInstanceReportPdf(instanceId: number, reportDefinitionId: number) {
    if (updateSpinner) updateSpinner(true);
    try {
      const response = await RightTimeApi.getAsync(
        `/TestInstance/${instanceId}/Report/${reportDefinitionId}/Pdf`
      );
      const ok = await downloadResponseAsFile(
        response,
        `report_${reportDefinitionId}_${instanceId}.pdf`
      );
      if (!ok) toast.error("Unable to generate report PDF.");
      else toast.success("Report PDF downloaded.");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function downloadInstanceReportPdf(instanceId: number) {
    if (!instanceId) return;
    if (updateSpinner) updateSpinner(true);
    try {
      const reportsResp = await RightTimeApi.getAsync(`/TestInstance/${instanceId}/Reports`);
      if (!reportsResp?.ok || !reportsResp?.jsonData) {
        toast.error("Unable to load reports for this test instance.");
        return;
      }
      const reports: InstanceReportOption[] = reportsResp.jsonData.reports || [];
      if (!Array.isArray(reports) || reports.length === 0) {
        toast.info("No reports are available for this test instance.");
        return;
      }
      if (reports.length === 1) {
        await fetchAndSaveInstanceReportPdf(instanceId, reports[0].reportDefinitionId);
        return;
      }
      setReportPdfPendingInstanceId(instanceId);
      setReportPdfOptions(reports);
      setReportPdfSelectOpen(true);
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  function onInstanceReportPdfSelected(report: InstanceReportOption) {
    setReportPdfSelectOpen(false);
    const id = reportPdfPendingInstanceId;
    setReportPdfPendingInstanceId(null);
    if (!id) {
      toast.error("Unable to determine test instance for PDF.");
      return;
    }
    void fetchAndSaveInstanceReportPdf(id, report.reportDefinitionId);
  }

  useEffect(() => {
    setSessionPermIds(readPermIdsFromSession());
  }, []);

  const canEditTestProperties =
    Boolean(isSystemAdmin) ||
    props.adminPerms?.includes(Enums.ROLE_PERMS.TestEdit) ||
    sessionPermIds.includes(Enums.ROLE_PERMS.TestEdit);

  const isSessionSystemAdmin =
    props.adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) ||
    sessionPermIds.includes(Enums.ROLE_PERMS.SystemAdmin);

  const canViewExtendedTabs = canEditTestProperties;

  const normalizeDetailValue = (value) => String(value || "").trim();
  const detailsHaveChanges =
    normalizeDetailValue(editedDetailsValues.createTime) !== normalizeDetailValue(originalDetailsValues.createTime) ||
    normalizeDetailValue(editedDetailsValues.codeExpiration) !== normalizeDetailValue(originalDetailsValues.codeExpiration) ||
    normalizeDetailValue(editedDetailsValues.embargoTime) !== normalizeDetailValue(originalDetailsValues.embargoTime) ||
    normalizeDetailValue(editedDetailsValues.firstName) !== normalizeDetailValue(originalDetailsValues.firstName) ||
    normalizeDetailValue(editedDetailsValues.lastName) !== normalizeDetailValue(originalDetailsValues.lastName);

  const parseDetailDate = (value: string) => {
    let parsed = DateTime.fromISO(String(value), { setZone: true });
    if (!parsed.isValid) {
      parsed = DateTime.fromISO(String(value), { zone: "utc" });
    }
    return parsed.isValid ? parsed : null;
  };

  const codeExpirationMoment = test?.codeExpiration
    ? parseDetailDate(String(test.codeExpiration))
    : null;
  const isCodeExpirationPast = Boolean(
    codeExpirationMoment && codeExpirationMoment < DateTime.now()
  );

  const hasExpiredCode = (() => {
    if (isCodeExpirationPast && test?.code) {
      return true;
    }

    if (!test?.code || test?.startTime) {
      return false;
    }

    const codeTimeoutMinutes = test.companyTestType?.codeTimeoutMinutes;
    if (codeTimeoutMinutes != null && codeTimeoutMinutes > 0 && test.createTime) {
      const createTime = parseDetailDate(String(test.createTime));
      if (createTime && createTime.plus({ minutes: codeTimeoutMinutes }) < DateTime.now()) {
        return true;
      }
    }

    return false;
  })();
  const isCodeInactive = Boolean(test?.code) && test?.codeActive === false;
  const isTestArchived = Boolean(test?.archived);
  const hasUnusableCode = hasExpiredCode || isCodeInactive || isTestArchived;

  const cttFields = test.companyTestTypeFields ?? test.companyTestType?.fields ?? [];
  const cttHasProctorFields = cttFields.some((field) => field?.proctorProvided);
  const cttId = test.companyTestType?.id;
  const stateId = Number(test?.stateId || 0);
  const isTestSubmitted =
    Boolean(test?.submitTime) ||
    Boolean(test?.scoreTime) ||
    stateId >= 301; // TEST_STATES.Submitted
  const isCodeDisplayDisabled = isCodeInactive || hasExpiredCode || isTestSubmitted || isTestArchived;
  const isCodeNotClickable = isTestSubmitted || isCodeInactive || isTestArchived;
  const canManageQuestionnaireCode =
    Boolean(canEditTestProperties) && !cttHasProctorFields && Boolean(cttId) && !isTestSubmitted;
  // Show disable whenever the personal code is still active (ignore CTT/proctor/submit gates used for create/refresh).
  const canDeactivateQuestionnaireCode =
    Boolean(canEditTestProperties) && Boolean(test?.code) && !isCodeInactive;
  // Show Code when open (incl. expired) or when a retained code exists for audit after finish.
  const canShowQuestionnaireCodeRow = !isTestSubmitted || Boolean(test?.code);
  const codeExpirationDisplay = test?.codeExpiration
    ? Helpers.EST(test.codeExpiration, "MM/dd/yyyy")
    : null;
  const codeStatusNote = (() => {
    if (isCodeInactive) {
      return codeExpirationDisplay
        ? `Inactive · ${isCodeExpirationPast || hasExpiredCode ? "Expired" : "Expires"} ${codeExpirationDisplay}`
        : "Inactive";
    }
    if (isTestArchived) {
      return codeExpirationDisplay
        ? `Archived · ${isCodeExpirationPast || hasExpiredCode ? "Expired" : "Expires"} ${codeExpirationDisplay}`
        : "Archived";
    }
    if (codeExpirationDisplay) {
      return `${isCodeExpirationPast || hasExpiredCode ? "Expired" : "Expires"} ${codeExpirationDisplay}`;
    }
    if (hasExpiredCode) {
      return "Expired";
    }
    return null;
  })();
  const codeStatusNoteExpired = isCodeInactive || isTestArchived || isCodeExpirationPast || hasExpiredCode;

  useEffect(() => {
    if (!props?.testInstanceId) return;

    updateTestUrl("pending...");

    // Set the active tab based on the prop when modal is displayed with a new test
    const requestedTab =
      initialActiveTab === "scoring"
        ? "details"
        : initialActiveTab || "details";
    const resolvedTab =
      requestedTab === "details" || canViewExtendedTabs
        ? requestedTab
        : "details";
    setActiveTab(resolvedTab);

    loadTestDetails();

    const handleKeyDown = (event) => {
      if (test) {
        event.stopPropagation();
        if (event.key === "Escape") {
          event.preventDefault();
          CloseModal();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [testInstanceId]);

  useEffect(() => {
    setChangesLoadedForTestId(null);
    setTestChanges([]);
  }, [testId]);

  useEffect(() => {
    if (activeTab !== "changes" || !testId) {
      return;
    }
    if (changesLoadedForTestId === testId) {
      return;
    }
    void loadTestChangesAsync();
  }, [activeTab, testId, changesLoadedForTestId]);


  function CloseModal() {
    if (selectedMatchInstanceIdRef.current) {
      setSelectedMatchInstanceId(null);
      return;
    }
    updateTest({});
    // Reset scores edit mode when closing modal
    setScoresEditMode(false);
    setEditScores({});
    setScoreTypes([]);
    onClose();
  }

  function openMatchDetails(match: MatchRow | null | undefined) {
    const instanceId = matchDetailsInstanceId(match);
    if (!instanceId) {
      toast.error("Unable to open questionnaire details for this match.");
      return;
    }
    setSelectedMatchInstanceId(instanceId);
  }

  function handleMonitorClick() {
    if (props?.onOpenMonitor && testInstanceId) {
      props.onOpenMonitor(testInstanceId);
      CloseModal();
      return;
    }

    setShowMonitorModal(true);
  }

  async function onStartTest() {
    window.location.replace(testUrl);
  }

  async function handleChangeCompanyConfirmAsync(companyId: number, siteId: number) {
    if (updateSpinner) updateSpinner(true);

    try {
      const payload = {
        testIds: [parseInt(testId)],
        changes: [
          { name: "CompanyId", value: companyId },
          { name: "CompanySiteId", value: siteId }
        ]
      };

      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (resp && resp.ok) {
        toast.success("Company and site changed successfully");
        // Reload test details to reflect the change
        await loadTestDetails();
        setShowChangeCompanyModal(false);
      } else {
        toast.error("Failed to change company and site");
        console.error("Error changing company and site:", resp?.statusText);
      }
    } catch (error) {
      toast.error("Error changing company and site");
      console.error("Error changing company and site:", error);
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function resetTest() {
    if (updateSpinner) updateSpinner(true);

    try {
      const payload = {
        testIds: [parseInt(testId)],
        changes: [{
          name: "reset",
          value: ""
        }]
      };

      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (resp && resp.ok) {
        toast.success("Test reset successfully");
        // Reload test details to reflect the reset
        await loadTestDetails();
        // Close the modal after successful reset
        CloseModal();
      } else {
        toast.error("Failed to reset test");
      }
    } catch (error) {
      console.error("Error resetting test:", error);
      toast.error("Error resetting test");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function deleteTest() {
    if (!testId) return;
    if (updateSpinner) updateSpinner(true);

    try {
      const payload = {
        testIds: [parseInt(testId)],
        changes: [{
          name: "Delete",
          value: ""
        }]
      };

      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (resp && resp.ok) {
        toast.success("Test deleted successfully");
        CloseModal();
      } else {
        toast.error("Failed to delete test");
      }
    } catch (error) {
      console.error("Error deleting test:", error);
      toast.error("Error deleting test");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function toggleArchivedAsync() {
    if (!testId || !isSystemAdmin) return;
    if (updateSpinner) updateSpinner(true);

    try {
      const changeName = test?.archived ? "unarchive" : "archive";
      const payload = {
        testIds: [parseInt(testId)],
        changes: [{
          name: changeName,
          value: ""
        }]
      };

      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (resp && resp.ok) {
        toast.success(test?.archived ? "Test unarchived." : "Test archived.");
        await loadTestDetails();
      } else {
        toast.error(resp?.jsonData?.message || "Failed to update archive flag");
      }
    } catch (error) {
      console.error("Error toggling archive:", error);
      toast.error("Error updating archive flag");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function resetScores() {
    if (updateSpinner) updateSpinner(true);

    try {
      const payload = {
        testIds: [parseInt(testId)],
        changes: [{
          name: "resetScores",
          value: ""
        }]
      };

      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (resp && resp.ok) {
        toast.success("Scores reset successfully");
        // Reload test details to reflect the reset
        await loadTestDetails();
      } else {
        toast.error("Failed to reset scores");
      }
    } catch (error) {
      console.error("Error resetting scores:", error);
      toast.error("Error resetting scores");
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function downloadInstanceData(instanceId) {
    if (!instanceId) {
      toast.error("Test instance ID not available for download");
      return;
    }
    if (!testId) {
      toast.error("Test Id not loaded yet.");
      return;
    }

    const resp = await RightTimeApi.getAsync(`/DownloadResults?id=${testId}`);
    const ok = await downloadResponseAsFile(resp, "TestResults.tsv");
    if (!ok) toast.error(apiMessage(resp, "Failed to download V20 results."));
    else toast.success("Downloaded V20 results.");
  }

  async function downloadInstanceData40(instanceId) {
    if (!instanceId) {
      toast.error("Test instance ID not available for download");
      return;
    }
    if (!testId) {
      toast.error("Test Id not loaded yet.");
      return;
    }

    const ok = await downloadAuthenticatedFile(
      `/DownloadResults40?id=${testId}`,
      `Test-${testId}-${instanceId}.tsv`
    );
    if (!ok) toast.error("Failed to download V40 results.");
    else toast.success("Downloaded V40 results.");
  }

  function onGradingOutputsUploadClick(instanceId: number, e: React.MouseEvent) {
    e.stopPropagation();
    if (!testId) {
      toast.error("Test Id not loaded yet.");
      return;
    }
    setGradingUploadTarget({ testId: Number(testId), instanceId });
    gradingUploadInputRef.current?.click();
  }

  async function onGradingOutputsFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const target = gradingUploadTarget;
    e.target.value = "";
    setGradingUploadTarget(null);
    if (!file || !target) return;

    if (props?.updateSpinner) props.updateSpinner(true);
    try {
      const fd = new FormData();
      fd.append("testId", String(target.testId));
      fd.append("testInstanceId", String(target.instanceId));
      fd.append("file", file);

      const resp = await RightTimeApi.postAsync("/GateKeeper2", fd);

      if (resp?.ok && resp.jsonData && typeof resp.jsonData.imported === "number") {
        toast.success(`Imported ${resp.jsonData.imported} grading output row(s).`);
        await loadTestDetails();
      } else {
        const rawMsg =
          resp?.jsonData &&
          typeof resp.jsonData === "object" &&
          resp.jsonData !== null &&
          "message" in resp.jsonData
            ? String((resp.jsonData as { message?: unknown }).message ?? "")
            : "";
        toast.error(rawMsg || resp?.statusText || "Upload failed");
      }
    } catch (error) {
      console.error("Grading output upload:", error);
      toast.error("Upload failed");
    } finally {
      if (props?.updateSpinner) props.updateSpinner(false);
    }
  }

  // Helper functions to determine if tabs should be shown
  const hasTestReference = !!(props?.testInstanceId || testId);

  const shouldShowSectionsTab = () => canViewExtendedTabs && hasTestReference;

  const shouldShowAnalysisTab = () => shouldShowSectionsTab() && !!test.startTime;

  const shouldShowMatchesTab = () => canViewExtendedTabs && hasTestReference;

  const shouldShowInstancesTab = () => canViewExtendedTabs && hasTestReference;

  const shouldShowTagsTab = () => canViewExtendedTabs && hasTestReference;

  const shouldShowBillingTab = () => canViewExtendedTabs && hasTestReference;

  /* Scoring tab disabled — replaced by TestInstanceData on test instances. Restore with tab button + panel below.
  const shouldShowScoringTab = () => {
    if (!isSystemAdmin) return false;
    if (!test.responses) return false;
    return test.responses.some(r => /^3(0[1-9]|1[0-8])r$/.test(String(r.field ?? "")));
  };
  */

  function onMenuClick(e) {
    const action = e.currentTarget.getAttribute("tab-name");
    handleTabChange(action);
  }

  async function loadTestDetails() {
    if (props?.updateSpinner) props.updateSpinner(true);
    const resp = await RightTimeApi.getAsync("/TestDetailsInfo/" + testInstanceId);
    if (props?.updateSpinner) props.updateSpinner(false);
    if (resp && resp.ok) {
      updateTest(resp.jsonData);
      setTestId(resp.jsonData.id);
      const autoSectionId = firstSectionIdWithResponses(
        resp.jsonData?.sections,
        resp.jsonData?.responses
      );
      setSelectedSectionId(autoSectionId);
      const nextOriginal = {
        createTime: resp.jsonData?.createTime || "",
        codeExpiration: resp.jsonData?.codeExpiration || "",
        embargoTime: resp.jsonData?.embargoTime || "",
        firstName: resp.jsonData?.firstName || "",
        lastName: resp.jsonData?.lastName || "",
      };
      setOriginalDetailsValues(nextOriginal);
      setEditedDetailsValues(nextOriginal);
      if (
        !resp.jsonData.submitTime &&
        resp.jsonData.code &&
        resp.jsonData.codeActive !== false &&
        !resp.jsonData.archived
      ) {
        updateTestUrl(`${baseURL}${resp.jsonData.code}`);
      } else {
        updateTestUrl(null);
      }

      // Initialize site and job from test data
      if (resp.jsonData.availableSites && resp.jsonData.site) {
        const site = resp.jsonData.availableSites.find(s => s.name === resp.jsonData.site);
        if (site) setSelectedSiteId(site.id);
      }
      if (resp.jsonData.availableJobs && resp.jsonData.job) {
        const job = resp.jsonData.availableJobs.find(j => j.name === resp.jsonData.job);
        if (job) setSelectedJobId(job.id);
      }
    } else {
      toast.error("Unable to load questionnaire details.");
    }
  }

  async function loadTestChangesAsync(forceReload = false) {
    if (!testId) {
      return;
    }
    if (!forceReload && changesLoadedForTestId === testId) {
      return;
    }

    setChangesLoading(true);
    if (props?.updateSpinner) props.updateSpinner(true);

    try {
      const resp = await RightTimeApi.getAsync(`/Test/${testId}/Changes`);
      if (resp?.ok && resp?.jsonData?.changes) {
        setTestChanges(resp.jsonData.changes);
        setChangesLoadedForTestId(testId);
      } else {
        setTestChanges([]);
        toast.error("Unable to load change history.");
      }
    } catch (error) {
      console.error("Error loading test changes:", error);
      setTestChanges([]);
      toast.error("Error loading change history.");
    } finally {
      setChangesLoading(false);
      if (props?.updateSpinner) props.updateSpinner(false);
    }
  }

  function onDetailsValueChange(e) {
    const field = e?.target?.getAttribute?.("data-field");
    if (!field) return;

    const lowerField = String(field).toLowerCase();
    const rawValue = e?.target?.value ?? "";

    if (lowerField === "createtime") {
      setEditedDetailsValues((prev) => ({ ...prev, createTime: rawValue }));
      return;
    }
    if (lowerField === "codeexpiration") {
      setEditedDetailsValues((prev) => ({ ...prev, codeExpiration: rawValue }));
      return;
    }
    if (lowerField === "embargotime") {
      setEditedDetailsValues((prev) => ({ ...prev, embargoTime: rawValue }));
      return;
    }
    if (lowerField === "firstname") {
      setEditedDetailsValues((prev) => ({ ...prev, firstName: rawValue }));
      return;
    }
    if (lowerField === "lastname") {
      setEditedDetailsValues((prev) => ({ ...prev, lastName: rawValue }));
    }
  }

  function clearDetailsDateTime(fieldName) {
    const lowerField = String(fieldName || "").toLowerCase();
    if (!lowerField) return;

    setEditedDetailsValues((prev) => {
      if (lowerField === "codeexpiration") {
        return { ...prev, codeExpiration: "" };
      }
      if (lowerField === "embargotime") {
        return { ...prev, embargoTime: "" };
      }
      return prev;
    });
  }

  function cancelDetailsChanges() {
    setEditedDetailsValues({
      createTime: originalDetailsValues.createTime,
      codeExpiration: originalDetailsValues.codeExpiration,
      embargoTime: originalDetailsValues.embargoTime,
      firstName: originalDetailsValues.firstName,
      lastName: originalDetailsValues.lastName,
    });
  }

  async function saveDetailsChangesAsync() {
    if (!testId || !detailsHaveChanges || isSavingDetails) {
      return;
    }

    const changes = [];

    const createTimeChanged = normalizeDetailValue(editedDetailsValues.createTime) !== normalizeDetailValue(originalDetailsValues.createTime);
    if (createTimeChanged) {
      if (!normalizeDetailValue(editedDetailsValues.createTime)) {
        toast.error("Create date/time cannot be empty.");
        return;
      }
      changes.push({ name: "CreateTime", value: editedDetailsValues.createTime });
    }

    const codeExpirationChanged = normalizeDetailValue(editedDetailsValues.codeExpiration) !== normalizeDetailValue(originalDetailsValues.codeExpiration);
    if (codeExpirationChanged) {
      changes.push({ name: "CodeExpiration", value: editedDetailsValues.codeExpiration || "" });
    }

    const embargoTimeChanged = normalizeDetailValue(editedDetailsValues.embargoTime) !== normalizeDetailValue(originalDetailsValues.embargoTime);
    if (embargoTimeChanged) {
      changes.push({ name: "EmbargoTime", value: editedDetailsValues.embargoTime || "" });
    }

    const firstNameChanged = normalizeDetailValue(editedDetailsValues.firstName) !== normalizeDetailValue(originalDetailsValues.firstName);
    if (firstNameChanged) {
      changes.push({ name: "FirstName", value: editedDetailsValues.firstName || "" });
    }

    const lastNameChanged = normalizeDetailValue(editedDetailsValues.lastName) !== normalizeDetailValue(originalDetailsValues.lastName);
    if (lastNameChanged) {
      changes.push({ name: "LastName", value: editedDetailsValues.lastName || "" });
    }

    if (changes.length === 0) {
      return;
    }

    setIsSavingDetails(true);
    if (props?.updateSpinner) props.updateSpinner(true);

    try {
      const payload = {
        testIds: [parseInt(testId)],
        changes,
      };
      const resp = await RightTimeApi.putAsync("/Tests", payload);

      if (!(resp && resp.ok)) {
        toast.error("Failed to save test details.");
        return;
      }

      const firstUpdate = Array.isArray(resp?.jsonData?.testUpdates)
        ? resp.jsonData.testUpdates.find((u) => Number(u?.testId) === Number(testId))
        : null;

      if (firstUpdate && firstUpdate.success === false) {
        toast.error(firstUpdate.message || "Failed to update test details.");
        return;
      }

      toast.success("Test details updated.");
      setChangesLoadedForTestId(null);
      await loadTestDetails();
    } catch (error) {
      console.error("Error saving test details:", error);
      toast.error("Error saving test details.");
    } finally {
      if (props?.updateSpinner) props.updateSpinner(false);
      setIsSavingDetails(false);
    }
  }

  // Helper function to handle tab changes
  const handleTabChange = (newTab) => {
    if (newTab !== "details" && !canViewExtendedTabs)
      return;

    // Reset scores edit mode when switching tabs
    if (newTab !== "scores" && scoresEditMode) {
      setScoresEditMode(false);
      setEditScores({});
      setScoreTypes([]);
    }
    setActiveTab(newTab);
  };

  // Function to handle entering edit mode for scores
  async function onEditScores() {
    setScoresEditMode(true);
    if (props?.updateSpinner) props.updateSpinner(true);

    try {
      // Load score types from API
      const resp = await RightTimeApi.getAsync("/TestScoreTypes");
      if (resp && resp.ok && resp.jsonData && resp.jsonData.scoreTypes) {
        setScoreTypes(resp.jsonData.scoreTypes);

        // Initialize edit scores with current test scores or empty values
        const currentScores = test.scores || [];
        const initialEditScores = {};

        resp.jsonData.scoreTypes.forEach(scoreType => {
          // Find existing score by matching the scoreType ID
          const existingScore = currentScores.find(s => s.scoreType === scoreType.id);
          initialEditScores[scoreType.name] = {
            value: existingScore?.score || "",
            message: existingScore?.message || ""
          };
        });

        setEditScores(initialEditScores);
      } else {
        toast.error("Failed to load score types");
        setScoresEditMode(false);
      }
    } catch (error) {
      toast.error("Error loading score types");
      setScoresEditMode(false);
    } finally {
      if (props?.updateSpinner) props.updateSpinner(false);
    }
  }

  // Function to handle canceling edit mode
  function onCancelEditScores() {
    setScoresEditMode(false);
    setEditScores({});
    setScoreTypes([]);
  }

  // Function to handle changes in edit mode
  function handleEditScoreChange(scoreType, field, value) {
    setEditScores(prev => ({
      ...prev,
      [scoreType]: {
        ...prev[scoreType],
        [field]: value
      }
    }));
  }

  async function checkForDuplicates() {
    if (!testId) return;
    if (props?.updateSpinner) props.updateSpinner(true);
    try {
      const result = await checkTestMatchesAsync(testId, (url) => RightTimeApi.getAsync(url));
      if (!result) return;
      if (result.status === "found") {
        toast.success(result.message);
        updateTest((prev) => ({ ...prev, matches: result.matches }));
        await loadTestDetails();
      } else if (result.status === "none") {
        toast.info(result.message);
      } else {
        toast.error(result.message);
      }
    } finally {
      if (props?.updateSpinner) props.updateSpinner(false);
    }
  }

  async function deleteDuplicates() {
    if (!testId) return;
    if (props?.updateSpinner) props.updateSpinner(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/Test/${testId}/Matches`);
      if (props?.updateSpinner) props.updateSpinner(false);
      if (resp && resp.ok) {
        toast.success("Duplicate tests deleted.");
        loadTestDetails();
      } else {
        toast.error("Failed to delete duplicate tests");
      }
    } catch (err) {
      if (props?.updateSpinner) props.updateSpinner(false);
      toast.error("Error deleting duplicate tests");
    }
  }

  async function copyToClipboard(url) {
    if (test.submitTime || isTestSubmitted || isCodeInactive || isTestArchived) {
      toast.info(
        isCodeInactive
          ? "Code is inactive - no URL to copy"
          : isTestArchived
            ? "Test is archived - no URL to copy"
            : "Test is completed - no URL to copy"
      );
      return;
    }

    // Use provided url or fall back to testUrl (expired-but-active codes still copy).
    const urlToCopy = url || testUrl;

    // Check if we have a valid URL to copy
    if (!urlToCopy || urlToCopy === "pending..." || urlToCopy.length === 0) {
      toast.error("No valid URL available to copy");
      return;
    }

    try {
      const copied = await copyUrlAsLink(urlToCopy);
      if (copied) {
        toast.success(`Copied URL to clipboard: ${urlToCopy}`);
      } else {
        toast.error("Could not copy URL to clipboard");
      }
    } catch (err) {
      console.error("Clipboard error:", err);
      if (err?.name === "NotAllowedError") {
        toast.warning("Please click anywhere in the window first, then try copying again");
      } else {
        toast.error("Could not copy URL to clipboard");
      }
    }
  }

  function onCloseQrModal() {
    setIsQrModalOpen(false);
  }

  function onShowQuestionnaireCodeQr(e) {
    const code = e.currentTarget.getAttribute("data-code");
    if (!code) return;
    setQrContent(`${baseURL}${code}`);
    setIsQrModalOpen(true);
  }

  async function onCreateTestCode() {
    if (!testId) {
      toast.error("Test ID not found");
      return;
    }

    try {
      const resp = await RightTimeApi.putAsync(`/Test/${testId}/CreateCode`);
      if (resp && resp.ok) {
        await loadTestDetails();
        toast.success("Code created successfully");
      } else {
        toast.error("Failed to create code");
      }
    } catch (error) {
      console.error("Error creating code:", error);
      toast.error("Error creating code");
    }
  }

  async function onDeactivateTestCode() {
    if (!testId) {
      toast.error("Test ID not found");
      return;
    }

    try {
      const resp = await RightTimeApi.putAsync(`/Test/${testId}/DeactivateCode`);
      if (resp && resp.ok) {
        updateTest((prev) => ({
          ...prev,
          codeActive: false,
          code: resp.jsonData?.code ?? prev.code,
          codeExpiration: resp.jsonData?.codeExpiration ?? prev.codeExpiration,
        }));
        updateTestUrl(null);
        toast.success("Code disabled");
      } else {
        toast.error("Failed to disable code");
      }
    } catch (error) {
      console.error("Error disabling code:", error);
      toast.error("Error disabling code");
    }
  }

  // Helper function to get score type name from ID using the enum
  const getScoreTypeName = (scoreTypeId) => {
    const scoreTypeNames = Object.keys(Enums.TEST_SCORE_TYPES);
    const scoreTypeName = scoreTypeNames.find(name => Enums.TEST_SCORE_TYPES[name] === scoreTypeId);
    return scoreTypeName || null;
  };

  // Render functions for different content sections
  const renderDetailsContent = () => (
    <>
      <div className="tableWrapper factTable centered testDetailsTable formTable">
        <table>
          <caption>
            <span className="title">Details</span>
          </caption>
          <tbody>

            {props.adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) && (
              <tr><th>Id</th><td>{test.id}</td></tr>
            )}

            {props.adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) && test.status && (
              <tr><th>Status</th><td>{test.status}</td></tr>
            )}

            {isSystemAdmin && (
              <tr>
                <th>Archived</th>
                <td
                  className="actions archiveToggle"
                  onClick={toggleArchivedAsync}
                  title={test.archived ? "Unarchive test" : "Archive test"}
                >
                  {test.archived ? (
                    <MdCheckBox className="checkbox checked actionIcon" />
                  ) : (
                    <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                  )}
                </td>
              </tr>
            )}

            <tr>
              <th>Company</th>
              <td>
                {isSystemAdmin ? (
                  <div
                    className="companyTextTrigger editBoxes"
                    onClick={() => setShowChangeCompanyModal(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowChangeCompanyModal(true);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {test.company}
                  </div>
                ) : (
                  test.company
                )}
              </td>
            </tr>

            {Array.isArray(test.customFields) && test.customFields.map((field) => (
              <tr key={field.displayName || field.fieldName}>
                <th>{field.displayName || field.fieldName}</th>
                <td>{field.value}</td>
              </tr>
            ))}
            {/* {canEditTestProperties ? (
              <>
                <tr>
                  <th>First Name</th>
                  <td>
                    <input
                      type="text"
                      data-field="FirstName"
                      value={editedDetailsValues.firstName}
                      onChange={onDetailsValueChange}
                      aria-label="First Name"
                      title="First Name"
                      className="companyTextTrigger detailsTextInput editBoxes"
                    />
                  </td>
                </tr>
                <tr>
                  <th>Last Name</th>
                  <td>
                    <input
                      type="text"
                      data-field="LastName"
                      value={editedDetailsValues.lastName}
                      onChange={onDetailsValueChange}
                      aria-label="Last Name"
                      title="Last Name"
                      className="companyTextTrigger detailsTextInput editBoxes"
                    />
                  </td>
                </tr>
              </>
            ) : ((test.firstName || test.lastName) && (
              <tr><th>Name</th><td>{test.firstName} {test.lastName}</td></tr>
            ))} */}

            {test.uniqueId && (
              <tr><th>UniqueId</th><td>{test.uniqueId}</td></tr>
            )}
            {test.language && (
            <tr><th>Language</th><td>{test.language}</td></tr>
            )}

            {props.adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) ?
              (<tr><th>Test Type</th><td>{test.companyTestType.name} ({test.testTypeName} : {test.testTypeId})</td></tr>) :
              (<tr><th>Test Type</th><td>{test.companyTestType.name}</td></tr>)}


            {
              test.location && (
                <tr><th>Location</th><td>{test.location}</td></tr>
              )
            }
            {test?.testInstances?.length === 1 && test.testInstances[0]?.siteName && (
              <tr><th>Site</th><td>{test.testInstances[0].siteName}</td></tr>
            )}
            {test?.testInstances?.length === 1 && test.testInstances[0]?.companyJobName && (
              <tr><th>Job</th><td>{test.testInstances[0].companyJobName}</td></tr>
            )}
            {test?.testInstances?.length === 1 && test.testInstances[0]?.supervisor && (
              <tr><th>Supervisor</th><td>{test.testInstances[0].supervisor}</td></tr>
            )}


            < tr ><th>Created</th><td>{canEditTestProperties ? (
              <DateTimePicker
                field="CreateTime"
                pickerTitle="Created"
                value={editedDetailsValues.createTime}
                onChange={onDetailsValueChange}
                displayAsText
                className="editBoxes"
              />
            ) : Helpers.EST(test.createTime, "MM/dd/yyyy")}</td></tr>

            {(canEditTestProperties || test.embargoTime) && (
              <tr><th data-help-id="EmbargoDate">Embargo Date</th><td>{canEditTestProperties ? (
                <div className="dateTimeFieldRow">
                  <DateTimePicker
                    field="EmbargoTime"
                    pickerTitle="Embargo Date"
                    value={editedDetailsValues.embargoTime}
                    onChange={onDetailsValueChange}
                    displayAsText
                    className="editBoxes"
                  />
                  {/* <ImCancelCircle
                    className="actionIcon clearDateTimeButton"
                    onClick={() => clearDetailsDateTime("EmbargoTime")}
                    title="Clear Embargo Date"
                  /> */}
                </div>
              ) : Helpers.EST(test.embargoTime)}</td></tr>
            )}

            {!test.startTime && (canEditTestProperties || test.codeExpiration) && (
              <tr><th>Code Expiration</th><td>{canEditTestProperties ? (
                <div className="dateTimeFieldRow">
                  <DateTimePicker
                    field="CodeExpiration"
                    pickerTitle="Code Expiration"
                    value={editedDetailsValues.codeExpiration}
                    onChange={onDetailsValueChange}
                    displayAsText
                    className="editBoxes"
                  />
                  {/* <ImCancelCircle
                    className="actionIcon clearDateTimeButton"
                    onClick={() => clearDetailsDateTime("CodeExpiration")}
                    title="Clear Code Expiration"
                  /> */}
                </div>
              ) : Helpers.EST(test.codeExpiration, "MM/dd/yyyy")}</td></tr>
            )}

            {test.welcomeTime && (
              <tr><th>Welcome Time</th><td>{isSystemAdmin ? Helpers.EST(test.welcomeTime) : Helpers.EST(test.welcomeTime, "MM/dd/yyyy")}</td></tr>
            )}

            {test.startTime && (
              <tr><th>Start Time</th><td>{isSystemAdmin ? Helpers.EST(test.startTime) : Helpers.EST(test.startTime, "MM/dd/yyyy")}</td></tr>
            )}

            {!test.startTime && test.embargoTime && (
              <tr><th>Embargo Time</th><td>{isSystemAdmin ? Helpers.EST(test.embargoTime) : Helpers.EST(test.embargoTime, "MM/dd/yyyy")}</td></tr>
            )}

            {test.submitTime && (
              <tr><th>Submit Time</th><td>{isSystemAdmin ? Helpers.EST(test.submitTime) : Helpers.EST(test.submitTime, "MM/dd/yyyy")}</td></tr>
            )}

            {/* {isSystemAdmin && (
        <tr><th>Score Pull Time</th><td>{isSystemAdmin ? Helpers.EST(test.scorePullTime) : Helpers.EST(test.scorePullTime, "MM/dd/yyyy")}</td></tr>
      )}

      {isSystemAdmin && (
        <tr><th>Score Time</th><td>{isSystemAdmin ? Helpers.EST(test.scoreTime) : Helpers.EST(test.scoreTime, "MM/dd/yyyy")}</td></tr>
      )} */}


            {canShowQuestionnaireCodeRow && (
              <tr>
                <th>Code</th>
                <td className="questionnaireCodeCell">
                  {test.code ? (
                    <>
                      <span className={`questionnaireCodeValue${isCodeDisplayDisabled ? " questionnaireCodeDisabled" : ""}`}>{test.code}</span>
                      {codeStatusNote ? (
                        <span className={`codeExpirationNote${codeStatusNoteExpired ? " expired" : ""}`}>{codeStatusNote}</span>
                      ) : null}
                      {!isCodeNotClickable && (
                        <LuClipboardCopy
                          className="actionIcon copyTestUrl"
                          title="Copy URL to clipboard"
                          onClick={() => copyToClipboard(`${baseURL}${test.code}`)}
                          data-help-id="copy-questionnaire-code-url"
                        />
                      )}
                      {canDeactivateQuestionnaireCode && (
                        <IoTrashOutline
                          className="actionIcon deleteIcon"
                          title="Disable Code"
                          onClick={onDeactivateTestCode}
                          data-help-id="deactivate-test-code"
                        />
                      )}
                      {canManageQuestionnaireCode && !hasUnusableCode && (
                        <BsQrCode
                          className="right qrCode"
                          onClick={onShowQuestionnaireCodeQr}
                          data-code={test.code}
                          data-help-id="view-questionnaire-qr-code"
                        />
                      )}
                    </>
                  ) : (
                    <span className="codeRequired">Code required to access</span>
                  )}
                  {canManageQuestionnaireCode && (
                    <MdRefresh
                      className="actionIcon"
                      title="Create/Refresh Code"
                      onClick={onCreateTestCode}
                      data-help-id="create-new-test-code"
                    />
                  )}
                </td>
              </tr>
            )}

            {test.data1 && (
              <tr><th>Data1</th><td>{test.data1}</td></tr>
            )}

            {test.data2 && (
              <tr><th>Data2</th><td>{test.data2}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Show sections in details tab only if there's 1 or less sections */}
      {
        test.startTime && (!test.sections || test.sections.length <= 1) ? (
          <div className="tableWrapper dimensionTable">
            <table>
              <caption>
                <span className="title">Sections</span>
              </caption>
              <thead>
                <tr>
                  <th></th>
                  <th>Section</th>
                  <th>Submitted</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {test.sections &&
                  test.sections.map((s, i) => {
                    return (
                      <tr key={"section" + i}>
                        <td>{s.index}</td>
                        <td>{s.name}</td>
                        <td>
                          {s.submitTime
                            ? Helpers.EST(s.submitTime, "MM/dd/yyyy - hh:mm:ss a")
                            : ""}
                        </td>
                        <td>{displayClientIp(s.ipAddr)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : null
      }
    </>
  );

  const renderSectionsContent = () => (
    <>
      {test.sections && test.sections.length > 0 ? (
        <div className="sectionsContainmentLayout">
          <div className="sectionsContainmentColumn">
            <div className="tableWrapper factTable">
              <table>
                <caption data-help-id="testSectionsTable">
                  <span className="title">Test Sections ({test.sections.length})</span>
                </caption>
                <thead>
                  <tr>
                    <th>Form Id</th>
                    <th>Section</th>
                    <th>Restarts</th>
                    <th>Submitted</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {test.sections.map((s, i) => {
                    return (
                      <tr key={"section" + i} data-id={s.sectionId} onClick={onSectionSelect} className={Number(selectedSectionId) === Number(s.sectionId) ? "active" : ""}>
                        <td>{s.formId ?? ""}</td>
                        <td>{s.name}</td>
                        <td>{countRestartedRealtimeForSection(test.realtimeResponses, s.sectionId)}</td>
                        <td>
                          {s.submitTime
                            ? Helpers.EST(s.submitTime, "MM/dd/yyyy - hh:mm:ss a")
                            : "n/a"}
                        </td>
                        <td>{displayClientIp(s.ipAddr)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sectionsContainmentColumn sectionsResponsesColumn">
            <div className="tableWrapper dimensionTable scrollable responsesWrapper">
              <table>
                <caption>
                  <span className="title">
                    Responses ({filterResponsesForSection(test.responses, selectedSectionId).length})
                  </span>
                </caption>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sectionResponses = filterResponsesForSection(test.responses, selectedSectionId);
                    if (sectionResponses.length === 0) {
                      return (
                        <tr>
                          <td colSpan={2} className="centerTextCell">
                            {selectedSectionId
                              ? "No responses entered for this section."
                              : ((test.responses || []).length > 0
                                ? "Select a section to filter responses."
                                : "No responses recorded for this questionnaire.")}
                          </td>
                        </tr>
                      );
                    }
                    return sectionResponses.map((r, i) => (
                      <tr key={`response-${i}`}>
                        <td>{r.field}</td>
                        <td>{responseDisplayValue(r)}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sectionsContainmentColumn sectionsRealtimeColumn">
            <div className="tableWrapper dimensionTable scrollable responsesWrapper realtimeResponsesWrapper">
              <table>
                <caption data-help-id="realtimeResponsesTable">
                  <span className="title">Realtime Responses</span>
                </caption>
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>FormId</th>
                    <th>Question Id</th>
                    <th>Value</th>
                    <th>Time</th>
                    <th>Client Time</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRealtimeResponses.length > 0 ? (
                    sortedRealtimeResponses.map((r, i) => {
                      return (<tr key={`response-${i}`}>
                        <td>{test.sections.find(s => s.sectionId === r.sectionId)?.name}</td>
                        <td>{r.formId}</td>
                        <td>{r.question}</td>
                        <td>{r.response}</td>
                        <td>{Helpers.EST(r.time, "MM/dd/yyyy - hh:mm:ss a")}</td>
                        <td>{r.clientTime ? Helpers.EST(r.clientTime, "MM/dd/yyyy - hh:mm:ss a") : ""}</td>
                      </tr>);
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="centerTextCell">No realtime responses recorded.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="noData">No sections configured for this questionnaire.</div>
      )}
    </>
  );

  const renderAnalysisContent = () => {
    if (!test.sections || test.sections.length === 0) {
      return <div className="noData">No sections configured for this questionnaire.</div>;
    }

    const rows = buildSectionAnalysisRows({
      sections: test.sections,
      realtimeResponses: test.realtimeResponses,
      startTime: test.startTime,
    });

    return (
      <div className="tableWrapper factTable">
        <table>
          <caption>
            <span className="title">Section Analysis ({rows.length})</span>
          </caption>
          <thead>
            <tr>
              <th>Section Name</th>
              <th>Restarts</th>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
              <th>Initial Pause</th>
              <th>Longest Pause</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`analysis-section-${row.sectionId || i}`}>
                <td>{row.sectionName}</td>
                <td>{row.restarts}</td>
                <td>
                  {row.startIso
                    ? Helpers.EST(row.startIso, "MM/dd/yyyy - hh:mm:ss a")
                    : ""}
                </td>
                <td>
                  {row.endIso
                    ? Helpers.EST(row.endIso, "MM/dd/yyyy - hh:mm:ss a")
                    : ""}
                </td>
                <td>{row.durationMmSs}</td>
                <td>{row.initialPauseMmSs}</td>
                <td>{row.longestPauseMmSs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const onSectionSelect = (e) => {
    const sectionId = Number(e.currentTarget.getAttribute("data-id") || 0);
    setSelectedSectionId(sectionId > 0 ? sectionId : null);
  }

  async function handleSaveTagAsync(newTag) {
    const resp = await RightTimeApi.patchAsync(`/Test/${testId}/Instance/${testInstanceId}/Tag/${newTag}`);
    if (resp && resp.ok) {
      toast.success("Tag added successfully");
      // Reload test details to reflect new tag
      loadTestDetails();
    } else {
      toast.error("Failed to add tag");
    }
  }
  async function handleDeleteTag(tag) {
    if (!tag) return;
    const resp = await RightTimeApi.deleteAsync(`/Test/${testId}/Instance/${testInstanceId}/Tag/${tag}`);
    if (resp && resp.ok) {
      toast.success("Tag deleted successfully");
      // Reload test details to reflect deleted tag
      loadTestDetails();
    } else {
      toast.error("Failed to delete tag");
    }
  }

  async function handleDeleteInvoiceLineItem(invoiceLineItemId) {
    if (!invoiceLineItemId) return;

    if (updateSpinner) updateSpinner(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/Test/${testId}/InvoiceLineItem/${invoiceLineItemId}`);
      if (updateSpinner) updateSpinner(false);

      if (resp && resp.ok) {
        toast.success("Invoice line item deleted successfully");
        // Reload test details to reflect deleted item
        loadTestDetails();
      } else {
        toast.error("Failed to delete invoice line item");
      }
    } catch (err) {
      if (updateSpinner) updateSpinner(false);
      console.error("Error deleting invoice line item:", err);
      toast.error("Error deleting invoice line item");
    }
  }

  const renderChangesContent = () => {
    const formatChangeValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return String(value);
    };

    const formatTarget = (change) => {
      if (change?.testInstanceId) {
        return `Instance ${change.testInstanceId}`;
      }
      return "Test";
    };

    return (
      <div className="changesTable">
        <div className="tableWrapper dimensionTable">
          <table className="adminTestList">
            <caption>
              <span className="title">Change History</span>
            </caption>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Target</th>
                <th>Field</th>
                <th>From</th>
                <th>To</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {changesLoading ? (
                <tr>
                  <td colSpan={7} className="centerTextCell">Loading changes...</td>
                </tr>
              ) : testChanges.length > 0 ? (
                testChanges.map((change) => (
                  <tr key={`change-${change.id}`}>
                    <td>{change.changedAt ? Helpers.EST(change.changedAt) : "—"}</td>
                    <td>{change.userName || change.userId || "—"}</td>
                    <td>{formatTarget(change)}</td>
                    <td>{change.fieldName || "—"}</td>
                    <td>{formatChangeValue(change.oldValue)}</td>
                    <td>{formatChangeValue(change.newValue)}</td>
                    <td>{change.context || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="centerTextCell">No changes recorded</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTagsContent = () => {
    return (
      <div className="tagsTable">
        <div className="tableWrapper dimensionTable">
          <table>
            <caption>
              <span className="title">Tags</span>
              <span className="captionRight addEntity right" onClick={() => setShowNewEntityModal(true)}>
                <FaPlusSquare className="actionIcon plusIcon" />
              </span>
            </caption>
            <thead>
              <tr>
                <th>Tag</th><th></th>
              </tr>
            </thead>
            <tbody>
              {test.tags && test.tags.length > 0 ? (
                test.tags.map((tag, index) => (
                  <tr key={`tag-${index}`}>
                    <td>{tag}</td>
                    <td>
                      {isSystemAdmin && (
                        <MdDelete className="actionIcon trashIcon" onClick={() => handleDeleteTag(tag)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="centerTextCell">
                    No tags available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* Kept for when Scoring tab is re-enabled (with shouldShowScoringTab + tab UI).
  const renderScoringContent = () => {
    const cellClassByColor = (colorCode: string) => {
      const code = String(colorCode || "").toUpperCase();
      if (code === "G") return "scoringColorG";
      if (code === "Y") return "scoringColorY";
      return "scoringColorW";
    };

    const responseMap = new Map<string, string>();
    (test.responses || []).forEach(r => {
      responseMap.set(String(r.field ?? ""), String(r.value ?? ""));
    });
    return (
      <div className="scoringTable">
        <div className="tableWrapper dimensionTable scrollable">
          <table>
            <caption>
              <span className="title">Adjectives Scoring</span>
            </caption>
            <thead>
              <tr>
                <th>Correct</th>
                <th>Rating</th>
                <th>Abbrev</th>
                <th>Answer</th>
              </tr>
            </thead>
            <tbody>
              {ADJECTIVE_LIST.map((adj) => {
                const rating = responseMap.get(`${adj.id}r`) ?? "";
                const rawAnswer = (responseMap.get(`${adj.id}c`) ?? "").toUpperCase();
                const answer = rawAnswer.includes("T") && rawAnswer.includes("S") ? "D" : rawAnswer;
                const isCorrect = answer === adj.correct;
                const colorClass = cellClassByColor(adj.color);
                return (
                  <tr key={adj.id} className={answer ? (isCorrect ? "correct" : "incorrect") : ""}>
                    <td className={colorClass}>{answer && !isCorrect ? "X" : ""}</td>
                    <td>{rating}</td>
                    <td className={colorClass}>{adj.abbrev}</td>
                    <td>{answer}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  */

  const renderBillingContent = () => {
    if (!test.invoiceLineItems || test.invoiceLineItems.length === 0) {
      return <div className="noData">No billing information available.</div>;
    }

    return (
      <div className="billingTable">
        <div className="tableWrapper dimensionTable">
          <table>
            <caption>
              <span className="title">Invoice Line Items ({test.invoiceLineItems.length})</span>
            </caption>
            <thead>
              <tr>
                <th>Charge Date</th>
                <th>Description</th>
                <th>Fee Item</th>
                <th>Quantity</th>
                <th>Amount</th>
                <th>Invoice</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {test.invoiceLineItems.map((item, index) => (
                <tr key={`invoice-${index}`}>
                  <td>{item.chargeDate ? Helpers.EST(item.chargeDate, "MM/dd/yyyy") : ""}</td>
                  <td>{item.description || ""}</td>
                  <td>{item.companyFeeItemName || ""}</td>
                  <td>{item.quantity != null ? item.quantity : ""}</td>
                  <td>{item.amount != null ? `$${item.amount.toFixed(2)}` : ""}</td>
                  <td>{item.invoiceId || ""}</td>
                  <td>
                    {isSystemAdmin && item.id && (
                      <MdDelete
                        className="actionIcon trashIcon pointerCursor"
                        onClick={() => handleDeleteInvoiceLineItem(item.id)}
                        title="Delete invoice line item"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  const renderInstancesContent = () => {
    // Use testInstances from the test object
    const testInstances = test.testInstances || [];

    const INSTANCE_SCORE_COLUMNS = [
      { legacyScoreType: "Applicant", headerLabel: "Applicant" },
      { legacyScoreType: "Company", headerLabel: "Company" },
      { legacyScoreType: "Overall", headerLabel: "Overall" },
    ] as const;

    const SCORE_DATA_KEYS: Record<string, string> = {
      Applicant: "ApplicantScore",
      Company: "CompanyScore",
      Overall: "OverallScore",
    };

    const getScoreValue = (
      instance: TestInstanceLite,
      scoreTypeName: string
    ) => {
      const dataKey = SCORE_DATA_KEYS[scoreTypeName];
      const fromData = dataKey ? instance.instanceDataActiveByKey?.[dataKey] : undefined;
      if (fromData != null && String(fromData).trim() !== "") {
        return String(fromData);
      }
      const scores = instance.scores;
      if (!scores || !Array.isArray(scores)) {
        return "";
      }
      const scoreTypeId = Enums.TEST_SCORE_TYPES[scoreTypeName];
      const score = scores.find(
        (s) =>
          s.active === true &&
          (s.scoreType === scoreTypeId || s.scoreTypeName === scoreTypeName)
      );
      return score ? (score.score ?? "") : "";
    };

    const hasActiveTestInstanceData = (instance: TestInstanceLite) => {
      const m = instance.instanceDataActiveByKey;
      if (!m || typeof m !== "object") return false;
      return Object.keys(m).length > 0;
    };

    const visibleScoreColumns = INSTANCE_SCORE_COLUMNS.filter((col) =>
      testInstances.some((inst) => {
        const sv = getScoreValue(inst, col.legacyScoreType);
        return sv != null && String(sv).trim() !== "";
      })
    );

    const showScoresColumns = visibleScoreColumns.length > 0;

    const instancesTableColCount =
      6 + visibleScoreColumns.length + 1 + (isSystemAdmin ? 2 : 0);

    const getDisplayedScoreCell = (
      instance: TestInstanceLite,
      legacyScoreTypeName: string
    ) => getScoreValue(instance, legacyScoreTypeName);

    // Helper function to get status display from company test type
    const getStatusDisplay = (instance) => {
      if (!instance) return "";

      const directStatus =
        instance.companyTestStatus?.display ||
        instance.companyTestStatus?.name ||
        instance.companyTestStatusName ||
        instance.status ||
        instance.statusName ||
        "";
      if (directStatus) return directStatus;

      if (instance.statusId != null) {
        const statusEntry = Object.entries(Enums.TEST_INSTANCE_STATUS || {})
          .find(([, value]) => value === instance.statusId);
        if (statusEntry) {
          return statusEntry[0].replace(/([A-Z])/g, " $1").trim();
        }
      }

      const statusId =
        instance.companyTestStatusId ||
        instance.companyTestStatusID ||
        instance.statusId ||
        instance.statusID;
      if (!statusId || !test.companyTestTypes) return "";

      const companyTestTypeId =
        instance.companyTestType?.id ||
        instance.companyTestTypeId ||
        instance.companyTestTypeID ||
        instance.companyTestType?.companyTestTypeId;
      const companyTestType =
        instance.companyTestType ||
        test.companyTestTypes?.find(
          (ctt) => ctt.id === companyTestTypeId || ctt.companyTestTypeId === companyTestTypeId
        );
      if (!companyTestType || !companyTestType.statuses) return "";

      const status = companyTestType.statuses.find(
        (s) => s.ctsId === statusId || s.id === statusId || s.statusId === statusId
      );
      return status?.display || status?.name || "";
    };

    // Helper function to check if instance has a billable status
    const isInstanceBillable = (instance) => {
      if (!instance.companyTestStatusId || !test.companyTestTypes) return false;

      // Find the company test type
      const companyTestType = test.companyTestTypes.find(ctt => ctt.id === instance.companyTestType?.id);
      if (!companyTestType || !companyTestType.statuses) return false;

      // Find the status in the statuses collection
      const status = companyTestType.statuses.find(s => s.ctsId === instance.companyTestStatusId);
      return status?.billable || false;
    };

    const canDownloadInstanceData = (instance) => {
      const instanceStatusId = instance.statusId ?? instance.statusID;
      if (instanceStatusId == null) return false;

      return instanceStatusId > Enums.TEST_INSTANCE_STATUS.PendingStart;
    };

    return (
      <div className="testInstancesTable">
        <div className="tableWrapper dimensionTable">
          <table className="testInstances">
            <caption>
              <span className="title">Test Instances ({testInstances.length})</span>
              {isSystemAdmin && (
                <div className="captionRight addEntity right" onClick={() => {
                  setSelectedTestInstanceId(0);
                }}>
                  <FaPlusSquare className="actionIcon plusIcon" />
                </div>
              )}
            </caption>
            <thead>
              {!showScoresColumns ? (
                <tr>
                  <th>Date</th>
                  <th>CTT</th>
                  <th>Test Type</th>
                  <th>Job</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th>ID</th>
                  {isSystemAdmin && <th></th>}
                  {isSystemAdmin && <th>Future</th>}
                </tr>
              ) : (
                <>
                  <tr>
                    <th rowSpan={2}>Date</th>
                    <th rowSpan={2}>CTT</th>
                    <th rowSpan={2}>Test Type</th>
                    <th rowSpan={2}>Job</th>
                    <th rowSpan={2}>Site</th>
                    <th rowSpan={2}>Status</th>
                    <th colSpan={visibleScoreColumns.length}>Scores</th>
                    <th rowSpan={2}>ID</th>
                    {isSystemAdmin && <th rowSpan={2}></th>}
                    {isSystemAdmin && <th rowSpan={2}>Future</th>}
                  </tr>
                  <tr>
                    {visibleScoreColumns.map((col) => (
                      <th key={col.legacyScoreType}>{col.headerLabel}</th>
                    ))}
                  </tr>
                </>
              )}
            </thead>
            <tbody>
              {testInstances.length === 0 ? (
                <tr>
                  <td colSpan={instancesTableColCount} className="instancesEmptyCell">
                    No test instances available
                  </td>
                </tr>
              ) : (
                testInstances.map((instance, index) => (
                  <tr
                    key={`instance-${instance.id || index}`}
                    onClick={() => {
                      setSelectedTestInstanceId(instance.id);
                    }}
                    className={`clickable-row${isInstanceBillable(instance) && isSystemAdmin ? ' billable' : ''}`}
                  >
                    <td>
                      {instance.createTime
                        ? Helpers.formatDateOnly(instance.createTime)
                        : ""}
                    </td>
                    <td>{instance.companyTestType.name}</td>
                    <td>{instance.companyTestType.testTypeName}</td>
                    <td>{instance.companyJobName || ""}</td>
                    <td>{instance.siteName || ""}</td>
                    <td>{getStatusDisplay(instance)}</td>
                    {showScoresColumns ? (
                      <>
                        {visibleScoreColumns.map((col) => (
                          <td key={col.legacyScoreType}>
                            {getDisplayedScoreCell(instance, col.legacyScoreType)}
                          </td>
                        ))}
                      </>
                    ) : null}
                    <td>{instance.id || ""}</td>
                    {isSystemAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {canDownloadInstanceData(instance) && (
                          <FaFileDownload
                            title="Download instance data"
                            className="actionIcon downloadIcon testDat20"
                            onClick={() => downloadInstanceData(instance.id)}
                          />
                        )}
                      </td>
                    )}
                    {isSystemAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {canDownloadInstanceData(instance) && (
                          <FaFileDownload
                            title="Download instance data 40"
                            className="actionIcon downloadIcon testData40"
                            onClick={() => downloadInstanceData40(instance.id)}
                          />
                        )}
                        <FaUpload
                          title="Upload grading outputs Excel (.xlsx / .xlsm)"
                          className="actionIcon downloadIcon"
                          onClick={(e) => onGradingOutputsUploadClick(instance.id, e)}
                        />
                        {hasActiveTestInstanceData(instance) && (
                          <FaFilePdf
                            title="Download report PDF"
                            className="actionIcon downloadIcon"
                            onClick={() => downloadInstanceReportPdf(instance.id)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Helper function to get custom column title
  const getColumnTitle = (propertyName) => {
    const columnTitleMap = {
      // Mappings for matches entity columns
      'testId': 'Test ID',
      'firstName': 'First Name',
      'lastName': 'Last Name',
      'createTime': 'Created',
      'welcomeTime': 'Welcome',
      'submitTime': 'Submitted',
      'testTypeId': 'Type ID',
      'companyTestTypeId': 'CTT ID',
      'companyTestTypeName': 'Test Type',
      'siteName': 'Site',
      'jobName': 'Job',
      'scores': 'Scores',
    };

    // Return custom title if mapping exists, otherwise format the property name
    if (columnTitleMap[propertyName]) {
      return columnTitleMap[propertyName];
    }

    // Auto-format property names: camelCase to Title Case
    return propertyName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Helper function to filter out unwanted columns
  const getVisibleColumns = (allColumns) => {
    // Define columns to exclude from the table display
    const excludedColumns = new Set([
      // Add property names you want to hide from the table
      // Examples:
      // 'internalId',
      // 'rawData',
      // 'debugInfo',
      // 'systemFlags',
      // 'metadata',
      // 'tempField',
      // Add more columns to exclude as needed
    ]);

    // Filter out excluded columns
    return allColumns.filter(col => !excludedColumns.has(col));
  };

  const renderMatchesContent = () => {
    if (!test.matches || test.matches.length === 0) {
      return (
        <div className="noMatches">
          <div className="noData">No matches found for this user.</div>
          <div className="buttonGroup">
            <button type="button" className="btn btn-primary" onClick={checkForDuplicates}>
              Check for other tests for this user
            </button>
          </div>
        </div>
      );
    }

    // Get all unique property keys from all matches to determine table columns
    const allKeys = new Set();
    test.matches.forEach(match => {
      Object.keys(match).forEach(key => allKeys.add(key));
    });

    // Define preferred column order - columns not in this list will appear at the end
    const preferredOrder = [
      'testId',
      'firstName',
      'lastName',
      'createTime',
      'welcomeTime',
      'submitTime',
      'companyTestTypeName',
      'siteName',
      'jobName',
      'scores'
    ];

    // Get all columns and apply custom ordering - only show preferred columns
    const allColumns = Array.from(allKeys);
    const orderedColumns = preferredOrder.filter(col => allColumns.includes(col)); // Only preferred columns that exist
    const columns = getVisibleColumns(orderedColumns);

    // Identify date/time columns (common patterns)
    const dateTimeColumns = columns.filter(col =>
      col.toLowerCase().includes('time') ||
      col.toLowerCase().includes('date') ||
      col.toLowerCase().endsWith('at')
    );

    // Create original test row data using the same column structure as matches
    const originalTestRow = buildCurrentMatchRow(test);

    return (
      <div className="matchesTable">
        <div className="tableWrapper dimensionTable scrollable">
          <table className="matchesTable">
            <caption>
              <span className="title">Test Matches ({countActiveMatches(test.matches)})</span>
              <div className="captionRight">
              <span className="actionIcon" title="Refresh matches" onClick={checkForDuplicates}>
                <LuRefreshCcw className="actionIcon" />
              </span>
              <span className="actionIcon matchDeleteAction" title="Delete all duplicate matches" onClick={deleteDuplicates}>
                <MdDelete className="actionIcon" />
              </span>
              </div>
            </caption>
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col} data-data-column={col}>{getColumnTitle(col)}</th>
                ))}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr className="currentTest">
                {columns.map(col => {
                  const value = originalTestRow[col];
                  const isDateTime = dateTimeColumns.includes(col);
                  if (isDateTime && value) {
                    return (
                      <td
                        key={col}
                        data-data-column={col}
                        className="dateTime"
                        title={`Original Test - ${Helpers.formatFullDateTime(value)}`}
                      >
                        {Helpers.formatDateOnly(value)}
                      </td>
                    );
                  }
                  return (
                    <td key={col}>
                      {value !== null && value !== undefined ? String(value) : ""}
                    </td>
                  );
                })}
                <td></td>
              </tr>
              {/* Match rows */}
              {test.matches.map((match, i) => (
                <tr
                  key={"match" + i}
                  className={`matchRow` + (match.testId && match.eliminated ? " eliminated" : "")}
                  onClick={() => {
                    if (match.testId && match.testId !== test.id) openMatchDetails(match);
                  }}
                  title="Click to view this test"
                >
                  {columns.map(col => {
                    const value = match[col];
                    const isDateTime = dateTimeColumns.includes(col);
                    if (isDateTime && value) {
                      return (
                        <td
                          key={col}
                          data-data-column={col}
                          className="dateTime"
                          title={Helpers.formatFullDateTime(value)}
                        >
                          {Helpers.formatDateOnly(value)}
                        </td>
                      );
                    }
                    if (col === 'scores') {
                      return (
                        <td key={col} data-data-column={col}>
                          {match.scores && match.scores.length > 0 ? match.scores.map((score, index) => (
                            <div key={`score-${i}-${index}`}>
                              {getScoreTypeName(score.scoreType)}:{score.score}
                            </div>
                          )) : ""}
                        </td>
                      );
                    }
                    if (col === "testId") {
                      const canOpen = Boolean(matchDetailsInstanceId(match));
                      return (
                        <td
                          key={col}
                          data-data-column={col}
                          className={canOpen ? "matchTestId" : undefined}
                          title={canOpen ? "Open questionnaire details" : ""}
                          onClick={(e) => {
                            e.stopPropagation();
                            openMatchDetails(match);
                          }}
                        >
                          {value !== null && value !== undefined ? String(value) : ""}
                        </td>
                      );
                    }
                    return (
                      <td key={col} data-data-column={col} title={value != null ? String(value) : ""}>
                        {value !== null && value !== undefined ? String(value) : ""}
                      </td>
                    );
                  })}
                  <td className="actionCol" onClick={e => e.stopPropagation()}>
                    {match.eliminated == true ? (
                      <FaThumbsUp
                        title="Not eliminated"
                        // style={{ cursor: 'pointer', opacity: match.eliminated === true ? 0.4 : 1 }}
                        onClick={e => { e.stopPropagation(); onToggleMatch(match.testId); }}
                      />) : (
                      <FaThumbsDown
                        title="Eliminated"
                        // style={{ cursor: 'pointer', opacity: match.eliminated === true ? 1 : 0.4, marginLeft: 8 }}
                        onClick={e => { e.stopPropagation(); onToggleMatch(match.testId); }}
                      />)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Toggle eliminated status for a match
  async function onToggleMatch(matchId) {
    if (!testId || !matchId) return;
    if (props?.updateSpinner) props.updateSpinner(true);
    try {
      const resp = await RightTimeApi.patchAsync(`/Test/${testId}/ToggleMatch/${matchId}`);
      if (props?.updateSpinner) props.updateSpinner(false);
      if (resp && resp.ok) {
        toast.success("Match status toggled.");
        loadTestDetails();
      } else {
        toast.error("Failed to toggle match status");
      }
    } catch (err) {
      if (props?.updateSpinner) props.updateSpinner(false);
      toast.error("Error toggling match status");
    }
  }

  //console.log("TestDetailsModal", testId, test);
  const canShowQuestionnaireFooter = !isTestSubmitted && !hasUnusableCode;
  const canShowAnswerPdfFooter = Boolean(isSessionSystemAdmin && isTestSubmitted);
  // V20/V40 downloads: only when the test was actually submitted (SubmitTime set).
  const canShowResultsDownloadFooter = Boolean(isSessionSystemAdmin && test?.submitTime && testInstanceId);
  const canShowResetFooter = Boolean(isSessionSystemAdmin && activeTab === "details");
  const canShowDeleteFooter = Boolean(isSessionSystemAdmin && activeTab === "details");
  const canShowDetailsEditFooter = Boolean(canEditTestProperties && activeTab === "details" && detailsHaveChanges);
  const modalFooter = canShowQuestionnaireFooter || canShowAnswerPdfFooter || canShowResultsDownloadFooter || canShowDetailsEditFooter || canShowResetFooter || canShowDeleteFooter ? (
    <>
      <div className="footerLeft">
        {canShowDeleteFooter && (
          <button
            type="button"
            className="btn btn-danger iconButton"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSavingDetails}
            data-help-id="deleteTest"
            title="Delete Test"
            aria-label="Delete Test"
          >
            <IoTrashOutline />
          </button>
        )}
        {canShowResetFooter && (
          <button
            type="button"
            className="btn btn-warning"
            onClick={() => setShowResetConfirm(true)}
            disabled={isSavingDetails}
            data-help-id="resetTest"
          >
            Reset Test
          </button>
        )}
      </div>

      <div className="footerCenter">
        {canShowQuestionnaireFooter && (
          <>
            <div className="assessmentUrl">
              <label>Questionnaire URL:</label>
              {!test.code ? (
                <div className="applicantURL codeRequired">Code required to access</div>
              ) : testUrl && testUrl !== "pending..." ? (
                <div className="applicantURL clickable" onClick={() => copyToClipboard(testUrl)}>
                  {testUrl}
                </div>
              ) : (
                <div className="applicantURL">Unavailable</div>
              )}
            </div>
            {test.startTestButtonText && test.canTake && (
              <>
                <div className="orDivider">OR</div>
                <div>
                  <button className="primaryButton" onClick={onStartTest}>
                    Start
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {(canShowAnswerPdfFooter || canShowResultsDownloadFooter) && (
          <div className="footerDownloadActions">
            {canShowResultsDownloadFooter && (
              <>
                <button
                  type="button"
                  className="primaryButton resultsDownloadButton"
                  onClick={() => void downloadInstanceData(testInstanceId)}
                  title="Download V20 results"
                  data-help-id="downloadResultsV20"
                >
                  <FaFileDownload />
                  Download V20
                </button>
                <button
                  type="button"
                  className="primaryButton resultsDownloadButton"
                  onClick={() => void downloadInstanceData40(testInstanceId)}
                  title="Download V40 results"
                  data-help-id="downloadResultsV40"
                >
                  <FaFileDownload />
                  Download V40
                </button>
              </>
            )}
            {canShowAnswerPdfFooter && (
              <button
                type="button"
                className="primaryButton answerPdfButton"
                onClick={() => void downloadAnswerPdfAsync()}
                disabled={isGeneratingAnswerPdf || !testInstanceId}
                title="Download answer PDF"
              >
                <FaFilePdf />
                {isGeneratingAnswerPdf ? "Generating PDF..." : "Download Answer PDF"}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="footerRight">
        {canShowDetailsEditFooter && (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={cancelDetailsChanges}
              disabled={isSavingDetails}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveDetailsChangesAsync}
              disabled={isSavingDetails}
            >
              {isSavingDetails ? "Saving..." : "Save Changes"}
            </button>
          </>
        )}
      </div>
    </>
  ) : null;
  const headerActions = [
    <IoIosRefresh
      key="refresh"
      className="actionIcon pointerCursor refreshIcon"
      title="Refresh Details"
      data-help-id="refreshTestDetails"
      onClick={() => loadTestDetails()}
    />,
    isSystemAdmin && testInstanceId
      ? <MdMonitorHeart
        key="monitor"
        className="actionIcon pointerCursor monitorIcon"
        title="Monitor Test"
        data-help-id="monitorTest"
        onClick={handleMonitorClick}
      />
      : null,
  ];

  return (
    test && test?.id > 0 && (
      <>
        <Modal
          isOpen={!!(test && test?.id > 0)}
          onClose={CloseModal}
          className="testDetailsModal"
          title={`Questionnaire Details (${test.id})`}
          headerActions={headerActions}
          bodyClassName="withTabs"
          footer={modalFooter}
        >

          {/* Tabs positioned directly below header, outside modalBody */}
          <div className="modalTabsContainer">
            <div className="tab-container">
              <button
                tab-name="details"
                onClick={onMenuClick}
                className={`modal-tab ${activeTab === "details" ? "active" : ""}`}
              >
                📋 Details
              </button>
              {shouldShowSectionsTab() && (
                <button
                  tab-name="sections"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "sections" ? "active" : ""}`}
                >
                  📑 Sections <span className="badge">{test.sections?.length ?? 0}</span>
                </button>
              )}
              {shouldShowAnalysisTab() && (
                <button
                  tab-name="analysis"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "analysis" ? "active" : ""}`}
                >
                  📈 Analysis
                </button>
              )}
              {shouldShowMatchesTab() && (
                <button
                  tab-name="matches"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "matches" ? "active" : ""}`}
                >
                  🔗 Matches {test.matches && <span className="badge">{countActiveMatches(test.matches)}</span>}
                </button>
              )}
              {/* {shouldShowScoresTab() && (
                <button
                  tab-name="scores"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "scores" ? "active" : ""}`}
                >
                  📊 Scores ({test.testInstances?.length || 0})
                </button>
              )} */}
              {shouldShowInstancesTab() && (
                <button
                  tab-name="instances"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "instances" ? "active" : ""}`}
                >
                  📊 Instances <span className="badge">{test.testInstances?.length || 0}</span>
                </button>
              )}
              {shouldShowBillingTab() && (
                <button
                  tab-name="billing"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "billing" ? "active" : ""}`}
                >
                  💰 Billing <span className="badge">{test.invoiceLineItems?.length || 0}</span>
                </button>
              )}
              {shouldShowTagsTab() && (
                <button
                  tab-name="tags"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "tags" ? "active" : ""}`}
                >
                  🏷️ Tags <span className="badge">{test.tags?.length || 0}</span>
                </button>
              )}
              {canViewExtendedTabs && (
                <button
                  tab-name="changes"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "changes" ? "active" : ""}`}
                >
                  📝 Changes
                </button>
              )}
              {/* Scoring tab — restore with shouldShowScoringTab + renderScoringContent (see commented blocks above).
              {shouldShowScoringTab() && (
                <button
                  tab-name="scoring"
                  onClick={onMenuClick}
                  className={`modal-tab ${activeTab === "scoring" ? "active" : ""}`}
                >
                  🧮 Scoring
                </button>
              )}
              */}
            </div>
          </div>

          <div className="tabsContentContainer">
            <div className={`tab details ${activeTab === "details" ? "active" : ""}`}>
              {activeTab === "details" && renderDetailsContent()}
            </div>
            {shouldShowSectionsTab() && (
              <div className={`tab sections ${activeTab === "sections" ? "active" : ""}`}>
                {activeTab === "sections" && renderSectionsContent()}
              </div>
            )}
            {shouldShowAnalysisTab() && (
              <div className={`tab analysis ${activeTab === "analysis" ? "active" : ""}`}>
                {activeTab === "analysis" && renderAnalysisContent()}
              </div>
            )}
            {shouldShowMatchesTab() && (
              <div className={`tab matches ${activeTab === "matches" ? "active" : ""}`}>
                {activeTab === "matches" && renderMatchesContent()}
              </div>
            )}
            {/* {shouldShowScoresTab() && (
              <div className={`tab scores ${activeTab === "scores" ? "active" : ""}`}>
                {activeTab === "scores" && renderScoresContent()}
              </div>
            )} */}
            {shouldShowInstancesTab() && (
              <div className={`tab instances ${activeTab === "instances" ? "active" : ""}`}>
                {activeTab === "instances" && renderInstancesContent()}
              </div>
            )}
            {shouldShowBillingTab() && (
              <div className={`tab billing ${activeTab === "billing" ? "active" : ""}`}>
                {activeTab === "billing" && renderBillingContent()}
              </div>
            )}
            {shouldShowTagsTab() && (
              <div className={`tab tags ${activeTab === "tags" ? "active" : ""}`}>
                {activeTab === "tags" && renderTagsContent()}
              </div>
            )}
            {canViewExtendedTabs && (
              <div className={`tab changes ${activeTab === "changes" ? "active" : ""}`}>
                {activeTab === "changes" && renderChangesContent()}
              </div>
            )}
            {/* Scoring tab panel — restore with tab button above.
            {shouldShowScoringTab() && (
              <div className={`tab scoring ${activeTab === "scoring" ? "active" : ""}`}>
                {activeTab === "scoring" && renderScoringContent()}
              </div>
            )}
            */}
          </div>
        </Modal>

        <NewEntityModal
          isOpen={showNewEntityModal}
          entityName="Tag"
          onSave={handleSaveTagAsync}
          onClose={() => setShowNewEntityModal(false)}
        />

        <TestInstanceModal
          isOpen={selectedTestInstanceId != null}
          onClose={() => {
            setSelectedTestInstanceId(null);
            setChangesLoadedForTestId(null);
            loadTestDetails();
          }}
          testId={testId}
          testInstanceId={selectedTestInstanceId}
          updateSpinner={props.updateSpinner}
        />

        <TestMonitorModal
          isOpen={showMonitorModal}
          onClose={() => setShowMonitorModal(false)}
          testInstanceId={testInstanceId}
          updateSpinner={props.updateSpinner}
        />

        <ChangeCompanyModal
          isOpen={showChangeCompanyModal}
          onClose={() => setShowChangeCompanyModal(false)}
          onConfirm={handleChangeCompanyConfirmAsync}
        />

        <ReportSelectModal
          isOpen={reportPdfSelectOpen}
          reports={reportPdfOptions}
          onClose={() => {
            setReportPdfSelectOpen(false);
            setReportPdfPendingInstanceId(null);
          }}
          onSelect={onInstanceReportPdfSelected}
        />

        <ConfirmModal
          isOpen={showResetConfirm}
          onConfirm={() => {
            setShowResetConfirm(false);
            resetTest();
          }}
          onCancel={() => setShowResetConfirm(false)}
          title="Reset Test"
          message={`Are you sure you want to reset test ID ${testId}?`}
          confirmText="Yes, Reset Test"
          dangerText="This will reset the test to its initial state."
        />

        <ConfirmModal
          isOpen={showDeleteConfirm}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            deleteTest();
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          title="Delete Test"
          message={`Are you sure you want to delete test ID ${testId}?`}
          confirmText="Yes, Delete Test"
          dangerText="This will permanently delete this test."
        />

        <ConfirmModal
          isOpen={showResetScoresConfirm}
          onConfirm={() => {
            setShowResetScoresConfirm(false);
            resetScores();
          }}
          onCancel={() => setShowResetScoresConfirm(false)}
          title="Reset All Scores"
          message={`Are you sure you want to reset all scores for test ID ${testId}?<br/><br/><br/>Note: This will reset this test so it can be regraded via GateKeeper.<br/><br/>This will also remove all previous scores.`}
          confirmText="Yes, Reset All Scores"
          dangerText="This will permanently delete all scores for this test."
        />

        <QrModal
          isOpen={isQrModalOpen}
          onClose={onCloseQrModal}
          content={qrContent}
        />

        {selectedMatchInstanceId ? (
          <TestDetailsModal
            key={selectedMatchInstanceId}
            testInstanceId={selectedMatchInstanceId}
            onClose={() => setSelectedMatchInstanceId(null)}
            updateSpinner={props.updateSpinner}
            isSystemAdmin={isSystemAdmin}
            adminPerms={props.adminPerms}
            onOpenMonitor={props.onOpenMonitor}
          />
        ) : null}

        <input
          ref={gradingUploadInputRef}
          type="file"
          accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
          className="hiddenGradingUploadInput"
          aria-label="Upload grading outputs Excel file"
          title="Select grading outputs workbook"
          onChange={onGradingOutputsFileSelected}
        />
      </>
    )
  );
}


